import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { extractFromImage, extractFromText, scoreConfirmedPlan, MAX_UPLOAD_BYTES, ALLOWED_TYPES } from "@/homeiq/services/vastuProxy";
import { VASTU_LOGO } from "@/assets/brand";

const DIRECTIONS = ["", "North", "North-East", "East", "South-East", "South", "South-West", "West", "North-West"];
const PLAN_FIELDS = [
    { key: "entrance", label: "Entrance" },
    { key: "kitchen", label: "Kitchen" },
    { key: "master_bedroom", label: "Master Bedroom" },
    { key: "toilets", label: "Toilets" },
    { key: "balcony", label: "Balcony" },
    { key: "living_room", label: "Living Room" },
    { key: "pooja_room", label: "Pooja Room (if detected)" },
];
const STATUS_TONE = {
    Excellent: { dot: "#16a34a", text: "#15803d", bg: "rgba(22,163,74,0.10)", border: "rgba(22,163,74,0.30)" },
    Good:      { dot: "#1E6BFF", text: "#1E6BFF", bg: "rgba(30,107,255,0.08)", border: "rgba(30,107,255,0.30)" },
    Moderate:  { dot: "#d97706", text: "#b45309", bg: "rgba(217,119,6,0.10)", border: "rgba(217,119,6,0.30)" },
    Concern:   { dot: "#dc2626", text: "#dc2626", bg: "rgba(220,38,38,0.10)", border: "rgba(220,38,38,0.30)" },
};
const CONFIDENCE_TONE = {
    High:   "#16a34a",
    Medium: "#d97706",
    Low:    "#dc2626",
};

function SectionHead({ eyebrow, title, sub }) {
    return (
        <div className="mb-2">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold mb-2">— {eyebrow}</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0A1F44] tracking-tight">{title}</h2>
            {sub && <p className="mt-3 text-[#0A1F44]/65 max-w-2xl text-sm md:text-base">{sub}</p>}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────
// Stage 1 — Landing: upload + text fallback
// ─────────────────────────────────────────────────────────────────
// Granular processing stages during a Vastu upload/extraction. The backend
// is a single HTTP round-trip, so we cycle the message locally on realistic
// timings — this replaces the opaque "Reading plan…" with a sequence that
// tells the user what actually happens under the hood.
const PDF_STAGES = [
    { at: 0,    label: "Reading PDF" },
    { at: 1500, label: "Converting floor plan" },
    { at: 3500, label: "Running AI analysis" },
    { at: 8000, label: "Preparing report" },
];
const IMAGE_STAGES = [
    { at: 0,    label: "Reading floor plan" },
    { at: 2000, label: "Running AI analysis" },
    { at: 7000, label: "Preparing report" },
];

function VastuLanding({ onExtract }) {
    const fileRef = useRef(null);
    const [text, setText] = useState("");
    const [mode, setMode] = useState("upload"); // 'upload' | 'manual'
    const [manual, setManual] = useState({
        entrance: "", kitchen: "", master_bedroom: "", toilets: "",
        balcony: "", living_room: "", pooja_room: "",
    });
    const [busy, setBusy] = useState(false);
    const [stageLabel, setStageLabel] = useState(null);
    const [err, setErr] = useState(null);

    const runStaged = (stages, work) => {
        const timers = stages.map((s) =>
            window.setTimeout(() => setStageLabel(s.label), s.at)
        );
        const cleanup = () => timers.forEach((t) => window.clearTimeout(t));
        return work().finally(cleanup);
    };

    const handleFile = async (file) => {
        if (!file) return;
        setErr(null); setBusy(true);
        const stages = file.type === "application/pdf" ? PDF_STAGES : IMAGE_STAGES;
        setStageLabel(stages[0].label);
        try {
            const result = await runStaged(stages, () => extractFromImage(file));
            onExtract(result);
        }
        catch (e) { setErr(e.message); }
        finally { setBusy(false); setStageLabel(null); }
    };

    const handleText = async () => {
        setErr(null); setBusy(true);
        setStageLabel("Running AI analysis");
        try { onExtract(await extractFromText(text)); }
        catch (e) { setErr(e.message); }
        finally { setBusy(false); setStageLabel(null); }
    };

    // Manual form skips Claude extraction entirely — we still route through the
    // extract endpoint by stringifying the form, so the confirmation flow stays
    // identical and the proxy contract is the single integration point.
    const handleManual = async () => {
        const filled = Object.entries(manual).filter(([, v]) => v).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`);
        if (filled.length < 4) {
            setErr("Fill at least 4 directions for a meaningful read.");
            return;
        }
        setErr(null); setBusy(true);
        setStageLabel("Running AI analysis");
        try { onExtract(await extractFromText(filled.join(". "))); }
        catch (e) { setErr(e.message); }
        finally { setBusy(false); setStageLabel(null); }
    };

    return (
        <section data-testid="vastu-landing" className="py-14 px-6 md:px-12 bg-white">
            <div className="max-w-3xl mx-auto space-y-8">
                <img
                    src={VASTU_LOGO}
                    alt="Vastu"
                    data-testid="vastu-landing-logo"
                    className="h-16 md:h-20 w-auto object-contain"
                />
                <SectionHead eyebrow="Vastu Analyzer" title="A traditional read on your future home." sub="Upload the floor plan — Claude reads the directions, you confirm, then we score. Advisory only — your HomeIQ Score is untouched." />

                {/* Mode toggle */}
                <div className="inline-flex gap-1 p-1 rounded-full bg-[#0A1F44]/6">
                    {[
                        { id: "upload", label: "Upload / describe" },
                        { id: "manual", label: "Fill directions manually" },
                    ].map((m) => (
                        <button key={m.id} type="button" onClick={() => setMode(m.id)}
                                data-testid={`vastu-mode-${m.id}`}
                                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                    mode === m.id ? "bg-[#0A1F44] text-white" : "text-[#0A1F44]/65 hover:text-[#0A1F44]"
                                }`}>
                            {m.label}
                        </button>
                    ))}
                </div>

                {mode === "upload" && (
                    <>
                        <div className="rounded-3xl border-2 border-dashed border-[#0A1F44]/15 bg-[#F7F8FB] p-8 md:p-12 text-center">
                            <input ref={fileRef} type="file" accept={ALLOWED_TYPES.join(",")}
                                   onChange={(e) => handleFile(e.target.files?.[0])}
                                   className="hidden" data-testid="vastu-upload-input" />
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                                    data-testid="vastu-upload-btn"
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A1F44] hover:bg-[#1E6BFF] disabled:opacity-50 text-white text-sm font-semibold transition-colors">
                                {busy && (
                                    <span
                                        aria-hidden="true"
                                        className="inline-block w-3.5 h-3.5 rounded-full border-2 border-white/25 border-t-white animate-spin"
                                    />
                                )}
                                {busy ? (stageLabel || "Processing") + "…" : "Upload floor plan"}
                            </button>
                            <p className="mt-3 text-[11px] text-[#0A1F44]/55">PNG · JPG · PDF · max {Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB</p>
                        </div>

                        <div className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold mb-2">Or describe in words</div>
                            <textarea value={text} onChange={(e) => setText(e.target.value)}
                                      placeholder="3BHK, main door East, kitchen South-East, master bedroom South-West, toilets attached to bedrooms, balcony North…"
                                      rows={4} data-testid="vastu-text-input"
                                      className="w-full bg-[#F7F8FB] border border-[#0A1F44]/10 rounded-2xl p-3 text-sm text-[#0A1F44] focus:outline-none focus:border-[#1E6BFF]/40 placeholder:text-[#0A1F44]/35" />
                            <div className="mt-3 flex justify-end">
                                <button type="button" onClick={handleText} disabled={busy || !text.trim()}
                                        data-testid="vastu-text-submit"
                                        className="px-4 py-2 rounded-full bg-[#1E6BFF] hover:bg-[#0A1F44] disabled:opacity-40 text-white text-xs font-semibold transition-colors">
                                    Analyse description →
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {mode === "manual" && (
                    <div data-testid="vastu-manual-form" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8 space-y-4">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Fill what you know</div>
                        {PLAN_FIELDS.map((f) => (
                            <div key={f.key} className="flex items-center justify-between gap-4 flex-wrap">
                                <label htmlFor={`vm-${f.key}`} className="text-sm font-semibold text-[#0A1F44] flex-1 min-w-[12rem]">{f.label}</label>
                                <select id={`vm-${f.key}`}
                                        data-testid={`vastu-manual-${f.key}`}
                                        value={manual[f.key]}
                                        onChange={(e) => setManual((m) => ({ ...m, [f.key]: e.target.value }))}
                                        className="px-3 py-2 rounded-xl border border-[#0A1F44]/15 bg-[#F7F8FB] text-sm text-[#0A1F44] focus:outline-none focus:border-[#1E6BFF]/40 min-w-[10rem]">
                                    {DIRECTIONS.map((d) => (<option key={d} value={d}>{d || "— not set —"}</option>))}
                                </select>
                            </div>
                        ))}
                        <div className="flex justify-end pt-2">
                            <button type="button" onClick={handleManual} disabled={busy}
                                    data-testid="vastu-manual-submit"
                                    className="px-4 py-2 rounded-full bg-[#0A1F44] hover:bg-[#1E6BFF] disabled:opacity-40 text-white text-xs font-semibold transition-colors">
                                {busy ? "Reading…" : "Continue to confirmation →"}
                            </button>
                        </div>
                    </div>
                )}

                {err && (
                    <div data-testid="vastu-error" className="rounded-2xl bg-[#dc2626]/10 border border-[#dc2626]/30 text-[#dc2626] p-4 text-sm">
                        {err}
                    </div>
                )}
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────────
// Stage 2 — Confirmation: edit before scoring
// ─────────────────────────────────────────────────────────────────
function VastuConfirm({ extraction, onCancel, onConfirm }) {
    const [plan, setPlan] = useState(() => ({ ...extraction.extracted_plan }));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const confTone = CONFIDENCE_TONE[extraction.confidence] ?? CONFIDENCE_TONE.Medium;
    const edited = JSON.stringify(plan) !== JSON.stringify(extraction.extracted_plan);

    const submit = async () => {
        setErr(null); setBusy(true);
        try {
            const final = await scoreConfirmedPlan({
                session_id: extraction.session_id,
                extracted_plan: plan,
                user_edited: edited,
                original_extraction: extraction.extracted_plan,
            });
            onConfirm({ confirmedPlan: plan, final });
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    };

    return (
        <section data-testid="vastu-confirm" className="py-14 px-6 md:px-12 bg-white">
            <div className="max-w-3xl mx-auto space-y-6">
                <img
                    src={VASTU_LOGO}
                    alt="Vastu"
                    data-testid="vastu-confirm-logo"
                    className="h-14 md:h-16 w-auto object-contain"
                />
                <SectionHead
                    eyebrow="Step 2 of 3 · Confirm"
                    title="We read this from your plan."
                    sub="Edit anything that looks off — scoring won't run until you confirm."
                />

                {extraction.source_pdf_info && extraction.source_pdf_info.total_pages > 1 && (
                    <div
                        data-testid="vastu-pdf-multipage-notice"
                        className="rounded-2xl bg-[#1E6BFF]/8 border border-[#1E6BFF]/25 text-[#0A1F44] text-sm p-4 leading-relaxed"
                    >
                        <div className="text-[10px] tracking-[0.25em] uppercase text-[#1E6BFF] font-semibold mb-1">
                            — Multi-page PDF
                        </div>
                        Your PDF has {extraction.source_pdf_info.total_pages} pages —
                        we analysed <strong>page 1</strong>.
                        If the floor plan is on a different page, re-export just that
                        page and re-upload for the most accurate read.
                    </div>
                )}
                <div data-testid="vastu-confidence-pill" className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-bold"
                     style={{ color: confTone, borderColor: `${confTone}55`, backgroundColor: `${confTone}1A` }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: confTone }} aria-hidden />
                    Confidence · {extraction.confidence}
                </div>
                <p data-testid="vastu-confidence-basis" className="text-[11px] text-[#0A1F44]/60 italic">
                    {extraction.confidence_basis || "Based on floor-plan readability and extraction confidence."}
                </p>

                <div className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8 space-y-4">
                    {PLAN_FIELDS.map((f) => (
                        <div key={f.key} className="flex items-center justify-between gap-4 flex-wrap" data-testid={`vastu-confirm-row-${f.key}`}>
                            <label htmlFor={`vc-${f.key}`} className="text-sm font-semibold text-[#0A1F44] flex-1 min-w-[12rem]">{f.label}</label>
                            <select
                                id={`vc-${f.key}`}
                                data-testid={`vastu-confirm-select-${f.key}`}
                                value={plan[f.key] ?? ""}
                                onChange={(e) => setPlan((p) => ({ ...p, [f.key]: e.target.value }))}
                                className="px-3 py-2 rounded-xl border border-[#0A1F44]/15 bg-[#F7F8FB] text-sm text-[#0A1F44] focus:outline-none focus:border-[#1E6BFF]/40 min-w-[10rem]"
                            >
                                {DIRECTIONS.map((d) => (
                                    <option key={d} value={d}>{d || "— not detected —"}</option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>

                {err && (
                    <div data-testid="vastu-confirm-error" className="rounded-2xl bg-[#dc2626]/10 border border-[#dc2626]/30 text-[#dc2626] p-4 text-sm">{err}</div>
                )}

                <div className="flex items-center justify-between gap-3">
                    <button type="button" onClick={onCancel} data-testid="vastu-confirm-cancel"
                            className="text-xs font-semibold text-[#0A1F44]/65 hover:text-[#0A1F44]">← Start over</button>
                    <button type="button" onClick={submit} disabled={busy}
                            data-testid="vastu-confirm-submit"
                            className="px-5 py-2.5 rounded-full bg-[#0A1F44] hover:bg-[#1E6BFF] disabled:opacity-50 text-white text-sm font-semibold transition-colors">
                        {busy ? "Scoring…" : "Confirm & score →"}
                    </button>
                </div>
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────────
// Stage 3 — Results
// ─────────────────────────────────────────────────────────────────
function VastuResults({ final, onRestart }) {
    const tone = final.overall_score >= 80 ? "#16a34a" : final.overall_score >= 60 ? "#d97706" : "#dc2626";
    return (
        <section data-testid="vastu-results" className="py-14 px-6 md:px-12 bg-white">
            <div className="max-w-5xl mx-auto space-y-8">
                <img
                    src={VASTU_LOGO}
                    alt="Vastu"
                    data-testid="vastu-results-logo"
                    className="h-14 md:h-16 w-auto object-contain"
                />
                {/* A. Overall Score + B. Verdict */}
                <div data-testid="vastu-overall" className="rounded-3xl bg-[#0A1F44] text-white p-6 md:p-10 relative overflow-hidden">
                    <div className="absolute inset-0 blueprint-grid opacity-[0.06] pointer-events-none" />
                    <div className="relative flex items-center justify-between flex-wrap gap-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">— Vastu advisory · independent of HomeIQ Score</div>
                            <div className="mt-3 flex items-baseline gap-4">
                                <span data-testid="vastu-overall-score" className="font-display font-black text-6xl md:text-7xl tabular-nums" style={{ color: tone }}>
                                    {final.overall_score}
                                </span>
                                <span className="text-white/55 text-xl">/ 100</span>
                            </div>
                            <h2 data-testid="vastu-verdict" className="mt-3 font-display font-extrabold text-2xl md:text-3xl">{final.verdict}</h2>
                        </div>
                        <button type="button" onClick={onRestart} data-testid="vastu-restart"
                                className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold transition-colors">
                            New read →
                        </button>
                    </div>
                </div>

                {/* F. 6-Dimension breakdown */}
                <div data-testid="vastu-dimensions" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {final.dimensions.map((d) => {
                        const t = STATUS_TONE[d.status] ?? STATUS_TONE.Moderate;
                        return (
                            <div key={d.name} data-testid={`vastu-dim-${d.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                 className="rounded-2xl border bg-white p-5"
                                 style={{ borderColor: t.border }}>
                                <div className="flex items-baseline justify-between gap-2">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">{d.name}</div>
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                                          style={{ color: t.text, backgroundColor: t.bg }}>
                                        <span className="w-1 h-1 rounded-full" style={{ backgroundColor: t.dot }} aria-hidden />
                                        {d.status}
                                    </span>
                                </div>
                                <div className="mt-2 font-display font-extrabold text-3xl text-[#0A1F44] tabular-nums">{d.score}</div>
                                <p className="mt-2 text-xs text-[#0A1F44]/65 leading-relaxed">{d.reasoning}</p>
                            </div>
                        );
                    })}
                </div>

                {/* C / D / E lists */}
                <div className="grid md:grid-cols-3 gap-3">
                    <ListCard testid="vastu-positives" label="Positives" tone="#16a34a" bg="#f0fdf4" items={final.positives} />
                    <ListCard testid="vastu-concerns" label="Concerns" tone="#d97706" bg="#fffbeb" items={final.concerns} />
                    <ListCard testid="vastu-recommendations" label="Recommendations" tone="#1E6BFF" bg="#eff6ff" items={final.recommendations} />
                </div>

                <div className="text-[11px] text-[#0A1F44]/50 italic">
                    Vastu insights are advisory only. The HomeIQ Score above remains entirely based on Affordability, Loan, Market and Investment.
                </div>
            </div>
        </section>
    );
}

function ListCard({ testid, label, tone, bg, items }) {
    return (
        <div data-testid={testid} className="rounded-2xl border p-5" style={{ borderColor: `${tone}33`, backgroundColor: bg }}>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold mb-2" style={{ color: tone }}>{label}</div>
            {(!items || items.length === 0) ? (
                <p className="text-xs text-[#0A1F44]/55 italic">None flagged.</p>
            ) : (
                <ul className="space-y-2 list-disc pl-4">
                    {items.map((t, i) => (
                        <li key={i} className="text-xs text-[#0A1F44]/80 leading-relaxed">{t}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────
// Orchestrator
// ─────────────────────────────────────────────────────────────────
export default function VastuTab() {
    const {
        vastuExtraction, setVastuExtraction,
        vastuConfirmedPlan, setVastuConfirmedPlan,
        vastuFinal, setVastuFinal,
    } = useHomeIQ();

    const reset = () => {
        setVastuExtraction(null);
        setVastuConfirmedPlan(null);
        setVastuFinal(null);
    };

    if (!vastuExtraction) {
        return <VastuLanding onExtract={(ex) => setVastuExtraction(ex)} />;
    }
    if (!vastuFinal) {
        return (
            <VastuConfirm
                extraction={vastuExtraction}
                onCancel={reset}
                onConfirm={({ confirmedPlan, final }) => {
                    setVastuConfirmedPlan(confirmedPlan);
                    setVastuFinal(final);
                }}
            />
        );
    }
    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <VastuResults final={vastuFinal} onRestart={reset} />
            {/* Confirmed plan preserved in state — exposed via context for future audit/diff. */}
            {vastuConfirmedPlan && <span data-testid="vastu-confirmed-state-marker" className="sr-only">{JSON.stringify(vastuConfirmedPlan)}</span>}
        </motion.div>
    );
}
